import { animate, group, query, style, transition, trigger } from '@angular/animations';

export enum PageTransitionPresetIdentifier {
  NONE = '',
  MOBILE = 'mobile',
  MOBILE_REVERSED = 'mobileReversed',
  IOS = 'ios',
  IOS_REVERSED = 'iosReversed',
  FADESCALE = 'fadeScale',
  DEPTH = 'depth',
  FLIP = 'flip',
  BLUR = 'blur',
  ZOOMFADE = 'zoomFade',
  DESKTOP_FADE = 'desktopFade',
  DESKTOP_SLIDE_RIGHT = 'desktopSlideRight',
  DESKTOP_SLIDE_RIGHT_REVERSED = 'desktopSlideRightReversed',
  DESKTOP_SLIDE_LEFT = 'desktopSlideLeft',
  DESKTOP_SLIDE_LEFT_REVERSED = 'desktopSlideLeftReversed',
  DESKTOP_SLIDE_UP = 'desktopSlideUp',
  DESKTOP_SLIDE_UP_REVERSED = 'desktopSlideUpReversed',
}

export type PageTransitionParams = {
  enterOpacity: number;
  enterTransform: string;
  enterFilter: string;
  enterBoxShadow: string;
  enterZIndex: number;
  enterOpacityEnd: number;
  enterTransformEnd: string;
  enterFilterEnd: string;
  enterBoxShadowEnd: string;

  leaveOpacity: number;
  leaveTransform: string;
  leaveFilter: string;
  leaveBoxShadow: string;
  leaveZIndex: number;
  leaveOpacityEnd: number;
  leaveTransformEnd: string;
  leaveFilterEnd: string;
  leaveBoxShadowEnd: string;

  duration: number;
  easing: string;
  willChange?: string;
  persistFinalStyles?: boolean;
};

export const pageTransitionPresets: Record<string, PageTransitionParams> = {
  [PageTransitionPresetIdentifier.NONE]: {
    enterOpacity: 1, // Fully visible immediately
    enterTransform: 'none', // No movement
    enterFilter: 'none', // No filter applied
    enterBoxShadow: 'none', // No shadow
    enterZIndex: 1, // Default z-index
    enterOpacityEnd: 1, // Stays fully visible
    enterTransformEnd: 'none', // No movement
    enterFilterEnd: 'none', // No filter applied
    enterBoxShadowEnd: 'none', // No shadow change

    leaveOpacity: 1, // Stays fully visible
    leaveTransform: 'none', // No movement
    leaveFilter: 'none', // No filter applied
    leaveBoxShadow: 'none', // No shadow
    leaveZIndex: 1, // Default z-index
    leaveOpacityEnd: 1, // No fade-out
    leaveTransformEnd: 'none', // No movement
    leaveFilterEnd: 'none', // No filter change
    leaveBoxShadowEnd: 'none', // No shadow change

    duration: 0, // No animation time
    easing: 'linear', // No effect
  },
  [PageTransitionPresetIdentifier.MOBILE]: {
    enterOpacity: 0.3, // Starts slightly transparent for a smoother fade-in
    enterTransform: 'translate3d(100%, 0, 0)', // Moves in from the right
    enterFilter: 'none',
    enterBoxShadow: 'none', // No box-shadow on enter
    enterZIndex: 100, // Higher z-index for the entering page
    enterOpacityEnd: 1, // Fully visible
    enterTransformEnd: 'translate3d(0%, 0, 0) scale(1)', // Arrives at normal position
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1, // Fully visible at start
    leaveTransform: 'translate3d(0, 0, 0)', // Starts in normal position
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveZIndex: 10, // Goes behind the new page
    leaveOpacityEnd: 0, // Fades out instead of shrinking
    leaveTransformEnd: 'translate3d(0, 0, 0)', // Stays in place but fades out
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 350, // Slightly shorter duration for snappier transition
    easing: 'cubic-bezier(0.25, 0.8, 0.5, 1)', // More aggressive easing curve
  },
  [PageTransitionPresetIdentifier.IOS]: {
    enterOpacity: 1,
    enterTransform: 'translate3d(100%, 0, 0) scale(1)', // Moves in from right
    enterFilter: 'none',
    enterBoxShadow: 'none', // No box-shadow on entering page
    enterZIndex: 100, // Enters above existing page
    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0%, 0, 0) scale(1)', // Arrives at normal position
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0) scale(1)', // Starts in normal position
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveZIndex: 10, // Leaves behind the new page
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'translate3d(-30%, 0, 0) scale(0.98)', // Subtle scale-down effect
    leaveFilterEnd: 'brightness(95%)', // Slight dimming effect
    leaveBoxShadowEnd: 'none',

    duration: 500, // Smooth transition duration
    easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)', // iOS-like smooth motion
  },
  [PageTransitionPresetIdentifier.FADESCALE]: {
    enterOpacity: 0,
    enterTransform: 'scale(0.95)',
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,
    enterOpacityEnd: 1,
    enterTransformEnd: 'scale(1)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'scale(1)',
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveZIndex: 10,
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'scale(1.05)',
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 300,
    easing: 'ease-out',
  },
  [PageTransitionPresetIdentifier.DEPTH]: {
    enterOpacity: 0,
    enterTransform: 'translate3d(0, 0, -50px)',
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,
    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0, 0, 0)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0)',
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveZIndex: 10,
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'translate3d(0, 0, 50px)',
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 400,
    easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
  },
  [PageTransitionPresetIdentifier.FLIP]: {
    enterOpacity: 1,
    enterTransform: 'rotateY(180deg) translateX(0)', // Prevents unwanted shifts
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,
    enterOpacityEnd: 1,
    enterTransformEnd: 'rotateY(0) translateX(0)', // Ends in the exact same position
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'rotateY(0) translateX(0)',
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveZIndex: 10,
    leaveOpacityEnd: 1,
    leaveTransformEnd: 'rotateY(-180deg) translateX(0)', // No shifting after rotation
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 500,
    easing: 'ease-out',
  },
  [PageTransitionPresetIdentifier.BLUR]: {
    enterOpacity: 0,
    enterTransform: 'none',
    enterFilter: 'blur(20px)',
    enterBoxShadow: 'none',
    enterZIndex: 100,
    enterOpacityEnd: 1,
    enterTransformEnd: 'none',
    enterFilterEnd: 'blur(0px)',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 0,
    leaveTransform: 'none',
    leaveFilter: 'blur(20px)',
    leaveBoxShadow: 'none',
    leaveZIndex: 10,
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'none',
    leaveFilterEnd: 'blur(20px)',
    leaveBoxShadowEnd: 'none',

    duration: 200,
    easing: 'ease-out',
  },
  [PageTransitionPresetIdentifier.ZOOMFADE]: {
    enterOpacity: 0,
    enterTransform: 'scale(1.5)',
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,
    enterOpacityEnd: 1,
    enterTransformEnd: 'scale(1)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 0,
    leaveTransform: 'scale(0.7)',
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveZIndex: 10,
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'scale(0.7)',
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 300,
    easing: 'ease-out',
  },
  [PageTransitionPresetIdentifier.MOBILE_REVERSED]: {
    enterOpacity: 0, // Starts completely invisible
    enterTransform: 'translate3d(-30%, 0, 0)', // Moves in from left (reverse direction)
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100, // Enters above
    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0%, 0, 0) scale(1)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0)',
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveZIndex: 10,
    leaveOpacityEnd: 0, // Fades out instead of shrinking
    leaveTransformEnd: 'translate3d(100%, 0, 0)', // Moves right (reverse of entering)
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 350,
    easing: 'cubic-bezier(0.25, 0.8, 0.5, 1)',
  },
  [PageTransitionPresetIdentifier.IOS_REVERSED]: {
    enterOpacity: 1,
    enterTransform: 'translate3d(-30%, 0, 0) scale(0.98)', // Moves in from left, slightly smaller
    enterFilter: 'brightness(95%)', // Starts slightly dimmed
    enterBoxShadow: 'none',
    enterZIndex: 100, // Entering page is on top
    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0%, 0, 0) scale(1)', // Becomes full-size and visible
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0) scale(1)', // Starts normal
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveZIndex: 10, // Leaves behind the new page
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'translate3d(100%, 0, 0) scale(1)', // Moves out to the right
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 500,
    easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
  },
  [PageTransitionPresetIdentifier.DESKTOP_FADE]: {
    enterOpacity: 0,
    enterTransform: 'translate3d(0, 10px, 0)',
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,
    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0, 0, 0)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0)',
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveZIndex: 10,
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'translate3d(0, 10px, 0)',
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 300,
    easing: 'ease-in-out',
  },
  [PageTransitionPresetIdentifier.DESKTOP_SLIDE_RIGHT]: {
    enterOpacity: 0,
    enterTransform: 'translate3d(70px, 0, 0) scale(1)',
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,

    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0, 0, 0) scale(1)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0) scale(1)',
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'translate3d(-70px, 0, 0) scale(.5)',
    leaveZIndex: 1,
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 350,
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',

    willChange: 'transform, opacity',
    persistFinalStyles: true,
  },
  [PageTransitionPresetIdentifier.DESKTOP_SLIDE_RIGHT_REVERSED]: {
    enterOpacity: 0,
    enterTransform: 'translate3d(-70px, 0, 0) scale(1)',
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,

    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0, 0, 0) scale(1)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0) scale(1)',
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'translate3d(70px, 0, 0) scale(.5)',
    leaveZIndex: 1,
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 350,
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',

    willChange: 'transform, opacity',
    persistFinalStyles: true,
  },
  [PageTransitionPresetIdentifier.DESKTOP_SLIDE_LEFT]: {
    enterOpacity: 0,
    enterTransform: 'translate3d(-40px, 0, 0) scale(1)',
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,

    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0, 0, 0) scale(1)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0) scale(1)',
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'translate3d(0, 0, 0) scale(.5)',
    leaveZIndex: 1,
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 350,
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',

    willChange: 'transform, opacity',
    persistFinalStyles: true,
  },
  [PageTransitionPresetIdentifier.DESKTOP_SLIDE_LEFT_REVERSED]: {
    enterOpacity: 0,
    enterTransform: 'translate3d(0, 0, 0) scale(1)',
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,

    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0, 0, 0) scale(1)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0) scale(1)',
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'translate3d(-40px, 0, 0) scale(.5)',
    leaveZIndex: 1,
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 350,
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',

    willChange: 'transform, opacity',
    persistFinalStyles: true,
  },
  [PageTransitionPresetIdentifier.DESKTOP_SLIDE_UP]: {
    enterOpacity: 0,
    enterTransform: 'translate3d(0, 70px, 0) scale(1)',
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,

    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0, 0px, 0) scale(1)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0) scale(1)',
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'translate3d(0, -70px, 0) scale(.5)',
    leaveZIndex: 1,
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 350,
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',

    willChange: 'transform, opacity',
    persistFinalStyles: true,
  },
  [PageTransitionPresetIdentifier.DESKTOP_SLIDE_UP_REVERSED]: {
    enterOpacity: 0,
    enterTransform: 'translate3d(0, 0, 0)',
    enterFilter: 'none',
    enterBoxShadow: 'none',
    enterZIndex: 100,
    enterOpacityEnd: 1,
    enterTransformEnd: 'translate3d(0, 0, 0)',
    enterFilterEnd: 'none',
    enterBoxShadowEnd: 'none',

    leaveOpacity: 1,
    leaveTransform: 'translate3d(0, 0, 0)',
    leaveFilter: 'none',
    leaveBoxShadow: 'none',
    leaveZIndex: 10,
    leaveOpacityEnd: 0,
    leaveTransformEnd: 'translate3d(0, 70px, 0)',
    leaveFilterEnd: 'none',
    leaveBoxShadowEnd: 'none',

    duration: 300,
    easing: 'cubic-bezier(0.2, 0.0, 0.2, 1)',
  },
};

// this version is needed for the preview area in workflow
export const previewPageTransition = trigger('previewPageTransition', [
  transition('* => *', [
    style({
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backfaceVisibility: 'hidden',
      opacity: '{{leaveOpacity}}',
      transform: '{{leaveTransform}}',
      filter: '{{leaveFilter}}',
      boxShadow: '{{leaveBoxShadow}}',
      zIndex: '{{leaveZIndex}}',
    }),
    animate(
      '{{duration}}ms {{easing}}',
      style({
        opacity: '{{enterOpacity}}',
        transform: '{{enterTransform}}',
        filter: '{{enterFilter}}',
        boxShadow: '{{enterBoxShadow}}',
        zIndex: '{{enterZIndex}}',
      }),
    ),
  ]),
]);

// this is the version that's needed for the web app
export const pageTransition = trigger('pageTransition', [
  transition('* => *', [
    style({ position: 'relative', minHeight: '100vh' }),

    // Apply common styles to enter and leave elements
    query(
      ':enter, :leave',
      [
        style({
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          minHeight: '100%',
          backfaceVisibility: 'hidden',
        }),
      ],
      { optional: true },
    ),

    // Dynamic enter styles
    query(
      ':enter',
      [
        style({
          opacity: '{{enterOpacity}}',
          transform: '{{enterTransform}}',
          filter: '{{enterFilter}}',
          boxShadow: '{{enterBoxShadow}}',
          zIndex: '{{enterZIndex}}',
        }),
      ],
      { optional: true },
    ),

    // Dynamic leave styles
    query(
      ':leave',
      [
        style({
          opacity: '{{leaveOpacity}}',
          transform: '{{leaveTransform}}',
          filter: '{{leaveFilter}}',
          boxShadow: '{{leaveBoxShadow}}',
          zIndex: '{{leaveZIndex}}',
        }),
      ],
      { optional: true },
    ),

    group([
      // Leave transition
      query(
        ':leave',
        [
          animate(
            '{{duration}}ms {{easing}}',
            style({
              opacity: '{{leaveOpacityEnd}}',
              transform: '{{leaveTransformEnd}}',
              filter: '{{leaveFilterEnd}}',
              boxShadow: '{{leaveBoxShadowEnd}}',
            }),
          ),
        ],
        { optional: true },
      ),

      // Enter transition
      query(
        ':enter',
        [
          animate(
            '{{duration}}ms {{easing}}',
            style({
              opacity: '{{enterOpacityEnd}}',
              transform: '{{enterTransformEnd}}',
              filter: '{{enterFilterEnd}}',
              boxShadow: '{{enterBoxShadowEnd}}',
            }),
          ),
        ],
        { optional: true },
      ),
    ]),
  ]),
]);
